import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheck,
  FaHome,
  FaPlus,
  FaSyncAlt,
  FaTimes,
  FaUmbrellaBeach,
  FaUserGraduate,
  FaUserTie,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import BackButton from "../ChildComponent/BackButton";
import SessionManager from "../Utils/SessionManager";
import MdlSelectSession from "../dialog/MdlSelectSession";
import MdlSelectSingleStudent from "../dialog/MdlSelectSingleStudent";
import MdlSelectSingleStaff from "../dialog/MdlSelectSingleStaff";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/LeaveManagement.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(125deg, ${COLORS.pesto_green_darker} 0%, ${COLORS.pesto_green} 42%, ${COLORS.card_cyan_green} 100%)`;

const PAGE_FONT =
  '"Segoe UI", system-ui, -apple-system, Roboto, "Helvetica Neue", Arial, sans-serif';

const STATUS = {
  PENDING: "1",
  APPROVED: "2",
  REJECTED: "3",
};

const EMPTY_FORM = {
  applicantType: "Student",
  applicantId: "",
  applicantName: "",
  authority: "Principal",
  subject: "",
  startDate: "",
  dayCount: 1,
  endDate: "",
  content: "",
};

// Created By Cursor
function toYmd(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Created By Cursor
function shiftYmd(offsetDays) {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + offsetDays);
  return toYmd(d);
}

// Created By Cursor — dayCount 1 = same day
function endDateFromDays(startYmd, dayCount) {
  const days = Math.max(1, parseInt(dayCount, 10) || 1);
  if (!startYmd) return "";
  const d = new Date(`${startYmd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + (days - 1));
  return toYmd(d);
}

const MAX_LEAVE_DAYS = 31;

// Created By Cursor
function validateLeaveForm(form) {
  const errors = {};
  if (!form.applicantId) {
    errors.applicantId =
      form.applicantType === "Staff"
        ? "Please choose a staff member."
        : "Please choose a student.";
  }
  if (!form.startDate) {
    errors.startDate = "Leave date is required.";
  } else {
    const d = new Date(`${form.startDate}T12:00:00`);
    if (Number.isNaN(d.getTime())) {
      errors.startDate = "Enter a valid leave date.";
    }
  }
  const rawDays = form.dayCount;
  if (rawDays === "" || rawDays === null || rawDays === undefined) {
    errors.dayCount = "Number of days is required.";
  } else {
    const days = parseInt(rawDays, 10);
    if (Number.isNaN(days) || String(rawDays).includes(".")) {
      errors.dayCount = "Number of days must be a whole number.";
    } else if (days < 1) {
      errors.dayCount = "Number of days must be at least 1.";
    } else if (days > MAX_LEAVE_DAYS) {
      errors.dayCount = `Number of days cannot be more than ${MAX_LEAVE_DAYS}.`;
    }
  }
  if (!String(form.authority || "").trim()) {
    errors.authority = "Authority is required.";
  }
  if (!String(form.subject || "").trim()) {
    errors.subject = "Subject is required.";
  }
  if (!String(form.content || "").trim()) {
    errors.content = "Application content is required.";
  }
  return errors;
}

// Created By Cursor — one month from today, never more than 31 days
function monthDayCountFromToday() {
  const start = new Date();
  start.setHours(12, 0, 0, 0);
  const end = new Date(start);
  end.setMonth(end.getMonth() + 1);
  const days = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
  return Math.min(MAX_LEAVE_DAYS, Math.max(1, days));
}

// Created By Cursor
function dateParts(value) {
  const raw = String(value || "").trim();
  const d = new Date(raw);
  if (!raw || Number.isNaN(d.getTime())) {
    return { day: "—", mon: "", year: "" };
  }
  return {
    day: String(d.getDate()).padStart(2, "0"),
    mon: d.toLocaleDateString("en-GB", { month: "short" }),
    year: String(d.getFullYear()),
  };
}

// Created By Cursor
function leaveDayCount(start, end) {
  const a = new Date(start);
  const b = new Date(end);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const days = Math.floor((b - a) / 86400000) + 1;
  return days > 0 ? days : null;
}

// Created By Cursor
function firstApplicant(raw) {
  if (!raw) return null;
  if (Array.isArray(raw)) return raw[0] || null;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed[0] : parsed;
    } catch (e) {
      return null;
    }
  }
  return raw;
}

// Created By Cursor
function resolveMediaUrl(pathOrUrl) {
  const raw = String(pathOrUrl || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const host = String(process.env.REACT_APP_SERVER_NAME || "").replace(/\/$/, "");
  if (!host) return raw;
  return `${host}/${raw.replace(/^\//, "")}`;
}

// Created By Cursor
function formatDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// Created By Cursor
function statusMeta(status) {
  const s = String(status || "");
  if (s === STATUS.APPROVED || s.toLowerCase() === "approved") {
    return { key: "approved", label: "Approved" };
  }
  if (s === STATUS.REJECTED || s.toLowerCase() === "rejected") {
    return { key: "rejected", label: "Rejected" };
  }
  if (
    s === "attendance" ||
    s.toLowerCase() === "no application" ||
    s.toLowerCase() === "no_application"
  ) {
    return { key: "attendance", label: "No application" };
  }
  return { key: "pending", label: "Pending" };
}

// Created By Cursor
function normalizeLeave(item, type) {
  const applicant = firstApplicant(item?.applicant);
  const isStaff = type === "Staff";
  let name = "—";
  let meta = "";
  let photo = "";

  if (isStaff) {
    name = applicant?.staff_name || item?.applicant_id || "—";
    meta =
      applicant?.department_name ||
      applicant?.staff_role ||
      item?.applicant_id ||
      "";
    photo = resolveMediaUrl(applicant?.staff_image_path);
  } else {
    name = applicant?.stu_name || item?.applicant_id || "—";
    const cls = Array.isArray(applicant?.class) ? applicant.class[0] : null;
    meta = cls
      ? `${cls.class_name || ""}${
          cls.section_name ? `-${cls.section_name}` : ""
        }`.trim()
      : item?.applicant_id || "";
    photo = resolveMediaUrl(applicant?.student_img_path);
  }

  const st = statusMeta(item?.status);
  const fromAttendance =
    item?.from_attendance === 1 ||
    item?.from_attendance === "1" ||
    item?.from_attendance === true ||
    st.key === "attendance";
  return {
    ...item,
    _type: type,
    _name: name,
    _meta: meta,
    _photo: photo,
    _statusKey: st.key,
    _statusLabel: item?.status_name || st.label,
    _fromAttendance: fromAttendance,
  };
}

// Created By Cursor — inbox row with calendar tile (leave-desk UI)
function LeaveCard({ item, busy, onView, onAddApplication }) {
  const start = dateParts(item.leave_start_date);
  const days = leaveDayCount(item.leave_start_date, item.leave_end_date);
  const initials = String(item._name || "?")
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <article
      className={`lm-row is-${item._statusKey}`}
      onClick={() => !busy && onView(item)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (!busy) onView(item);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="lm-row__cal" aria-hidden>
        <span className="lm-row__cal-mon">{start.mon}</span>
        <strong className="lm-row__cal-day">{start.day}</strong>
        <span className="lm-row__cal-year">{start.year}</span>
      </div>

      <div className="lm-row__avatar" aria-hidden>
        {item._photo ? (
          <img src={item._photo} alt="" />
        ) : (
          <span>{initials}</span>
        )}
      </div>

      <div className="lm-row__body">
        <div className="lm-row__head">
          <strong className="lm-row__name">{item._name}</strong>
          <span className={`lm-pill is-${item._statusKey}`}>
            {item._statusLabel}
          </span>
          <span className={`lm-pill is-${String(item._type || "").toLowerCase()}`}>
            {item._type === "Staff" ? (
              <FaUserTie aria-hidden />
            ) : (
              <FaUserGraduate aria-hidden />
            )}
            {item._type}
          </span>
        </div>
        <p className="lm-row__subject">
          {item._fromAttendance
            ? "Leave without application · marked in attendance"
            : item.subject || "Leave application"}
        </p>
        <div className="lm-row__meta">
          {item._meta ? <span className="lm-row__dept">{item._meta}</span> : null}
          <span className="lm-row__range">
            {formatDate(item.leave_start_date)} →{" "}
            {formatDate(item.leave_end_date)}
            {days ? ` · ${days} day${days === 1 ? "" : "s"}` : ""}
          </span>
        </div>
      </div>

      <div className="lm-row__side">
        {item._fromAttendance ? (
          <button
            type="button"
            className="lm-row__add-app"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              if (onAddApplication) onAddApplication(item);
            }}
          >
            <FaPlus aria-hidden /> Add application
          </button>
        ) : null}
        <span className="lm-row__go" aria-hidden>
          View
        </span>
      </div>
    </article>
  );
}

// Created By Cursor
function MdlLeaveDetails({
  open,
  item,
  busy,
  onClose,
  onApprove,
  onReject,
  onAddApplication,
}) {
  if (!open || !item) return null;
  const canDecide = item._statusKey === "pending" && !item._fromAttendance;

  return (
    <div className="lm-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="lm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lm-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lm-modal__head">
          <h2 id="lm-detail-title">Leave details</h2>
          <button
            type="button"
            className="lm-modal__close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="lm-modal__body">
          <div className="lm-detail-grid">
            <div>
              <small>Applicant</small>
              <strong>{item._name}</strong>
            </div>
            <div>
              <small>Type</small>
              <strong>{item._type}</strong>
            </div>
            <div>
              <small>Status</small>
              <strong className={`is-${item._statusKey}`}>
                {item._statusLabel}
              </strong>
            </div>
            <div>
              <small>Authority</small>
              <strong>{item.authority || "—"}</strong>
            </div>
            <div className="lm-detail-grid__wide">
              <small>Period</small>
              <strong>
                {formatDate(item.leave_start_date)} –{" "}
                {formatDate(item.leave_end_date)}
              </strong>
            </div>
            <div className="lm-detail-grid__wide">
              <small>Subject</small>
              <strong>{item.subject || "—"}</strong>
            </div>
          </div>
          <div className="lm-detail-content">
            <small>Application</small>
            <p>{item.application_content || "—"}</p>
          </div>
        </div>
        <footer className="lm-modal__foot">
          <button
            type="button"
            className="lm-btn lm-btn--secondary"
            onClick={onClose}
            disabled={busy}
          >
            Close
          </button>
          {item._fromAttendance ? (
            <button
              type="button"
              className="lm-btn lm-btn--primary"
              onClick={() => {
                if (onAddApplication) onAddApplication(item);
              }}
              disabled={busy}
            >
              <FaPlus aria-hidden /> Add application
            </button>
          ) : null}
          {canDecide ? (
            <>
              <button
                type="button"
                className="lm-btn lm-btn--danger"
                onClick={() => onReject(item)}
                disabled={busy}
              >
                <FaTimes aria-hidden /> Reject
              </button>
              <button
                type="button"
                className="lm-btn lm-btn--primary"
                onClick={() => onApprove(item)}
                disabled={busy}
              >
                <FaCheck aria-hidden /> Approve
              </button>
            </>
          ) : null}
        </footer>
      </div>
    </div>
  );
}

// Created By Cursor
function MdlAddLeave({
  open,
  form,
  errors = {},
  saving,
  onChange,
  onDayCountBlur,
  onClose,
  onSave,
  onPickStudent,
  onPickStaff,
}) {
  if (!open) return null;
  const isStaff = form.applicantType === "Staff";
  const err = (key) =>
    errors[key] ? <span className="lm-field__error">{errors[key]}</span> : null;
  const invalid = (key) => (errors[key] ? " is-invalid" : "");

  return (
    <div className="lm-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="lm-modal lm-modal--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lm-add-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lm-modal__head">
          <h2 id="lm-add-title">Add leave</h2>
          <button
            type="button"
            className="lm-modal__close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="lm-modal__body">
          <div className="lm-type-toggle" role="group" aria-label="Applicant type">
            <button
              type="button"
              className={`lm-type-btn${
                form.applicantType === "Student" ? " is-active" : ""
              }`}
              onClick={() => onChange("applicantType", "Student")}
              disabled={saving}
            >
              <FaUserGraduate aria-hidden /> Student
            </button>
            <button
              type="button"
              className={`lm-type-btn${
                form.applicantType === "Staff" ? " is-active" : ""
              }`}
              onClick={() => onChange("applicantType", "Staff")}
              disabled={saving}
            >
              <FaUserTie aria-hidden /> Staff
            </button>
          </div>

          <div className="lm-field">
            <span className="lm-field__label">Applicant</span>
            <div className="lm-pick-row">
              <input
                className={`lm-input${invalid("applicantId")}`}
                readOnly
                value={
                  form.applicantName
                    ? `${form.applicantName} (${form.applicantId})`
                    : ""
                }
                placeholder={
                  isStaff ? "Select staff member" : "Select student"
                }
              />
              <button
                type="button"
                className="lm-btn lm-btn--secondary"
                onClick={isStaff ? onPickStaff : onPickStudent}
                disabled={saving}
              >
                Choose
              </button>
            </div>
            {err("applicantId")}
          </div>

          <div className="lm-field">
            <span className="lm-field__label">Quick date</span>
            <div className="lm-quick-dates" role="group" aria-label="Quick date">
              {[
                { id: "yesterday", label: "Yesterday", offset: -1, days: 1 },
                { id: "today", label: "Today", offset: 0, days: 1 },
                { id: "tomorrow", label: "Tomorrow", offset: 1, days: 1 },
                { id: "week", label: "One Week", offset: 0, days: 7 },
                {
                  id: "month",
                  label: "One Month",
                  offset: 0,
                  days: monthDayCountFromToday(),
                },
              ].map((opt) => {
                const ymd = shiftYmd(opt.offset);
                const isOn =
                  form.startDate === ymd && Number(form.dayCount) === opt.days;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    className={`lm-quick-chip${isOn ? " is-active" : ""}`}
                    onClick={() =>
                      onChange("quickPreset", {
                        startDate: ymd,
                        dayCount: opt.days,
                      })
                    }
                    disabled={saving}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lm-grid-2">
            <label className="lm-field">
              <span className="lm-field__label">Leave date</span>
              <input
                type="date"
                className={`lm-input${invalid("startDate")}`}
                value={form.startDate}
                onChange={(e) => onChange("startDate", e.target.value)}
                disabled={saving}
              />
              {err("startDate")}
            </label>
            <label className="lm-field">
              <span className="lm-field__label">Number of days</span>
              <input
                type="number"
                className={`lm-input${invalid("dayCount")}`}
                min={1}
                max={MAX_LEAVE_DAYS}
                step={1}
                value={form.dayCount}
                onChange={(e) => onChange("dayCount", e.target.value)}
                onBlur={onDayCountBlur}
                disabled={saving}
              />
              {err("dayCount")}
            </label>
          </div>

          {form.startDate && form.endDate ? (
            <p className="lm-leave-hint">
              {Number(form.dayCount) > 1
                ? `Ends ${formatDate(form.endDate)} · ${form.dayCount} days`
                : `One day · ${formatDate(form.startDate)}`}
            </p>
          ) : null}

          <label className="lm-field">
            <span className="lm-field__label">Authority</span>
            <input
              type="text"
              className={`lm-input${invalid("authority")}`}
              value={form.authority}
              onChange={(e) => onChange("authority", e.target.value)}
              disabled={saving}
            />
            {err("authority")}
          </label>

          <label className="lm-field">
            <span className="lm-field__label">Subject</span>
            <input
              type="text"
              className={`lm-input${invalid("subject")}`}
              value={form.subject}
              onChange={(e) => onChange("subject", e.target.value)}
              disabled={saving}
              placeholder="Reason for leave"
            />
            {err("subject")}
          </label>

          <label className="lm-field">
            <span className="lm-field__label">Application</span>
            <textarea
              className={`lm-textarea${invalid("content")}`}
              rows={5}
              value={form.content}
              onChange={(e) => onChange("content", e.target.value)}
              disabled={saving}
              placeholder="Leave application text"
            />
            {err("content")}
          </label>
        </div>
        <footer className="lm-modal__foot">
          <button
            type="button"
            className="lm-btn lm-btn--secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="lm-btn lm-btn--primary"
            onClick={onSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Submit leave"}
          </button>
        </footer>
      </div>
    </div>
  );
}

// Created By Cursor
export default function LeaveManagement() {
  const history = useHistory();
  const location = useLocation();
  const loggedIn = localStorage.getItem("token") != null;

  const initialType = useMemo(() => {
    const q = new URLSearchParams(location.search || "");
    const t = String(q.get("type") || "").toLowerCase();
    if (t === "staff") return "staff";
    if (t === "student") return "student";
    return "all";
  }, [location.search]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [studentLeaves, setStudentLeaves] = useState([]);
  const [staffLeaves, setStaffLeaves] = useState([]);
  const [typeTab, setTypeTab] = useState(initialType);
  const [statusTab, setStatusTab] = useState("attendance");
  const [search, setSearch] = useState("");

  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  const [detailItem, setDetailItem] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [pickStudentOpen, setPickStudentOpen] = useState(false);
  const [pickStaffOpen, setPickStaffOpen] = useState(false);

  useEffect(() => {
    try {
      const current = SessionManager.getCurrentSession()?.[0];
      if (current?.session_id) {
        setSessionId(current.session_id);
        setSessionName(current.session_name || "");
      }
    } catch (e) {
      // ignore
    }
    setSessionReady(true);
  }, []);

  useEffect(() => {
    setTypeTab(initialType);
  }, [initialType]);

  const onSessionPicked = useCallback((id, name) => {
    setSessionId(id || "");
    setSessionName(name || "");
    setSessionDialogOpen(false);
    setStudentLeaves([]);
    setStaffLeaves([]);
  }, []);

  const loadLeaves = useCallback(
    async ({ soft = false } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !sessionId) {
        setStudentLeaves([]);
        setStaffLeaves([]);
        setLoading(false);
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      const params = { school_code: schoolCode, session_id: sessionId };
      try {
        const [studentRes, staffRes, attendanceRes] = await Promise.all([
          axios
            .get(`${API_BASE()}getStudentLeave.php`, { params })
            .then((res) => res.data)
            .catch(() => null),
          axios
            .get(`${API_BASE()}getStaffLeave.php`, { params })
            .then((res) => res.data)
            .catch(() => null),
          axios
            .get(`${API_BASE()}getAttendanceLeave.php`, { params })
            .then((res) => res.data)
            .catch(() => null),
        ]);

        const students =
          studentRes?.api_status === "OK" && Array.isArray(studentRes.leave)
            ? studentRes.leave.map((row) => normalizeLeave(row, "Student"))
            : [];
        const staff =
          staffRes?.api_status === "OK" && Array.isArray(staffRes.leave)
            ? staffRes.leave.map((row) => normalizeLeave(row, "Staff"))
            : [];
        if (attendanceRes?.api_status === "OK") {
          const attStudents = Array.isArray(attendanceRes.student)
            ? attendanceRes.student.map((row) =>
                normalizeLeave(row, "Student")
              )
            : [];
          const attStaff = Array.isArray(attendanceRes.staff)
            ? attendanceRes.staff.map((row) => normalizeLeave(row, "Staff"))
            : [];
          students.push(...attStudents);
          staff.push(...attStaff);
        }

        setStudentLeaves(students);
        setStaffLeaves(staff);
      } catch (error) {
        console.error(error);
        setStudentLeaves([]);
        setStaffLeaves([]);
        myToast.notify_warning("Failed to load leave applications.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!loggedIn || !sessionReady) return;
    if (!sessionId) {
      setLoading(false);
      return;
    }
    loadLeaves();
  }, [loggedIn, sessionReady, sessionId, loadLeaves]);

  const merged = useMemo(() => {
    const list =
      typeTab === "student"
        ? studentLeaves
        : typeTab === "staff"
          ? staffLeaves
          : [...studentLeaves, ...staffLeaves];
    return list.sort((a, b) => {
      const da = new Date(a.date_added || 0).getTime();
      const db = new Date(b.date_added || 0).getTime();
      return db - da;
    });
  }, [typeTab, studentLeaves, staffLeaves]);

  const counts = useMemo(() => {
    const base = merged;
    return {
      pending: base.filter((i) => i._statusKey === "pending").length,
      approved: base.filter((i) => i._statusKey === "approved").length,
      rejected: base.filter((i) => i._statusKey === "rejected").length,
      attendance: base.filter((i) => i._statusKey === "attendance").length,
      all: base.length,
      student: studentLeaves.length,
      staff: staffLeaves.length,
    };
  }, [merged, studentLeaves, staffLeaves]);

  const filtered = useMemo(() => {
    let list = merged;
    if (statusTab !== "all") {
      list = list.filter((i) => i._statusKey === statusTab);
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((i) => {
      const hay = [
        i._name,
        i._meta,
        i.subject,
        i.application_id,
        i.applicant_id,
        i._type,
        i._statusLabel,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [merged, statusTab, search]);

  const updateStatus = useCallback(
    async (item, nextStatus) => {
      const schoolCode = SessionManager.getSchoolCode();
      const staffId = SessionManager.getStaffDetails()?.staff_id;
      if (!schoolCode || !sessionId || !staffId) {
        myToast.notify_warning("Missing school, session, or staff details.");
        return;
      }
      const applicationId = item?.application_id;
      if (!applicationId) return;

      setBusyId(applicationId);
      try {
        const response = await axios
          .get(`${API_BASE()}updateLeaveStatus.php`, {
            params: {
              school_code: schoolCode,
              session_id: sessionId,
              staff_id: staffId,
              application_id: applicationId,
              status: nextStatus,
            },
          })
          .then((res) => res.data);

        if (response?.api_status === "OK") {
          myToast.notify_success(
            nextStatus === STATUS.APPROVED
              ? "Leave approved."
              : "Leave rejected. Recorded leave days were marked absent."
          );
          setDetailItem(null);
          await loadLeaves({ soft: true });
        } else {
          myToast.notify_warning(
            response?.api_remark || "Could not update leave status."
          );
        }
      } catch (error) {
        console.error(error);
        myToast.notify_warning("Failed to update leave status.");
      } finally {
        setBusyId("");
      }
    },
    [sessionId, loadLeaves]
  );

  const openAdd = useCallback(() => {
    if (!sessionId) {
      myToast.notify_warning("Select a session first.");
      setSessionDialogOpen(true);
      return;
    }
    setForm({
      ...EMPTY_FORM,
      applicantType: typeTab === "staff" ? "Staff" : "Student",
    });
    setFormErrors({});
    setAddOpen(true);
  }, [sessionId, typeTab]);

  const openAddFromAttendance = useCallback(
    (item) => {
      if (!sessionId) {
        myToast.notify_warning("Select a session first.");
        setSessionDialogOpen(true);
        return;
      }
      const startDate = toYmd(item?.leave_start_date);
      const dayCount = Math.min(
        MAX_LEAVE_DAYS,
        Math.max(1, leaveDayCount(item?.leave_start_date, item?.leave_end_date) || 1)
      );
      setForm({
        ...EMPTY_FORM,
        applicantType: item?._type === "Staff" ? "Staff" : "Student",
        applicantId: item?.applicant_id || "",
        applicantName: item?._name || "",
        startDate,
        dayCount,
        endDate: endDateFromDays(startDate, dayCount),
        subject: "",
        content: "",
        authority: "Principal",
      });
      setFormErrors({});
      setDetailItem(null);
      setAddOpen(true);
    },
    [sessionId]
  );

  const onFormChange = useCallback((key, value) => {
    setFormErrors((prev) => {
      if (!prev || !Object.keys(prev).length) return prev;
      const nextErr = { ...prev };
      if (key === "applicantType" || key === "applicantId") {
        delete nextErr.applicantId;
      }
      if (key === "startDate" || key === "quickPreset") delete nextErr.startDate;
      if (key === "dayCount" || key === "quickPreset") delete nextErr.dayCount;
      if (key === "authority") delete nextErr.authority;
      if (key === "subject") delete nextErr.subject;
      if (key === "content") delete nextErr.content;
      return nextErr;
    });
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "applicantType") {
        next.applicantId = "";
        next.applicantName = "";
      }
      if (key === "dayCount") {
        // Allow blank while editing; blank is treated as 1 for end-date / save.
        if (value === "" || value === null || value === undefined) {
          next.dayCount = "";
        } else {
          const parsed = parseInt(value, 10);
          next.dayCount = Number.isNaN(parsed) ? value : parsed;
        }
      }
      if (key === "quickPreset") {
        next.startDate = value.startDate;
        next.dayCount = Math.min(
          MAX_LEAVE_DAYS,
          Math.max(1, parseInt(value.dayCount, 10) || 1)
        );
        next.endDate = endDateFromDays(next.startDate, next.dayCount);
        delete next.quickPreset;
      }
      if (key === "startDate" || key === "dayCount") {
        next.endDate = endDateFromDays(
          key === "startDate" ? value : next.startDate,
          next.dayCount === "" ? 1 : next.dayCount
        );
      }
      return next;
    });
  }, []);

  const onDayCountBlur = useCallback(() => {
    setForm((prev) => {
      if (prev.dayCount !== "" && prev.dayCount != null) return prev;
      const dayCount = 1;
      return {
        ...prev,
        dayCount,
        endDate: endDateFromDays(prev.startDate, dayCount),
      };
    });
  }, []);

  const saveLeave = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !sessionId) {
      myToast.notify_warning("Missing school or session.");
      return;
    }
    const errors = validateLeaveForm(form);
    setFormErrors(errors);
    if (Object.keys(errors).length) {
      myToast.notify_warning(Object.values(errors)[0]);
      return;
    }
    const startDate = form.startDate;
    const dayCount = parseInt(form.dayCount, 10);
    const endDate = endDateFromDays(startDate, dayCount) || form.endDate;

    setSaving(true);
    try {
      const response = await axios
        .get(`${API_BASE()}AddStaffLeave.php`, {
          params: {
            school_code: schoolCode,
            session_id: sessionId,
            applicant_id: form.applicantId,
            applicant_type: form.applicantType,
            authority: form.authority || "Principal",
            subject: form.subject.trim(),
            leave_start_date: startDate,
            leave_end_date: endDate,
            application_content: form.content.trim(),
          },
        })
        .then((res) => res.data);

      if (response?.api_status === "OK") {
        myToast.notify_success("Leave application submitted.");
        setAddOpen(false);
        setForm(EMPTY_FORM);
        setFormErrors({});
        await loadLeaves({ soft: true });
      } else {
        myToast.notify_warning(
          response?.api_remark || "Could not submit leave."
        );
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Failed to submit leave.");
    } finally {
      setSaving(false);
    }
  }, [form, sessionId, loadLeaves]);

  const busy = loading || refreshing || !!busyId || saving;

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page lm-page"
      style={{ fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Leave Management</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell lm-shell">
        <header className="lm-hero" style={{ background: HERO_GRADIENT }}>
          <div className="lm-hero__copy">
            <h1 className="lm-hero__title">Leave Desk</h1>
            <p className="lm-hero__sub">
              {sessionName
                ? `${sessionName} · review student & staff leave in one place`
                : "Pick a session to open the leave inbox"}
            </p>
          </div>
          <div className="lm-hero__actions">
            <div
              className="lm-type-group"
              role="tablist"
              aria-label="Applicant type"
            >
              <button
                type="button"
                role="tab"
                aria-selected={typeTab === "student"}
                className={`lm-type-chip${
                  typeTab === "student" ? " is-active" : ""
                }`}
                onClick={() => setTypeTab("student")}
                disabled={busy}
              >
                <FaUserGraduate aria-hidden />
                Student
                <em>{counts.student}</em>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={typeTab === "staff"}
                className={`lm-type-chip${
                  typeTab === "staff" ? " is-active" : ""
                }`}
                onClick={() => setTypeTab("staff")}
                disabled={busy}
              >
                <FaUserTie aria-hidden />
                Staff
                <em>{counts.staff}</em>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={typeTab === "all"}
                className={`lm-type-chip${
                  typeTab === "all" ? " is-active" : ""
                }`}
                onClick={() => setTypeTab("all")}
                disabled={busy}
              >
                All
                <em>{counts.student + counts.staff}</em>
              </button>
            </div>
            <button
              type="button"
              className="lm-session"
              onClick={() => setSessionDialogOpen(true)}
              disabled={busy}
              title="Change session"
            >
              <FaCalendarAlt aria-hidden />
              <span>
                <small>Session</small>
                <strong>{sessionName || "Select"}</strong>
              </span>
            </button>
            <button
              type="button"
              className={`lm-icon-btn${refreshing ? " is-spin" : ""}`}
              onClick={() => loadLeaves({ soft: true })}
              disabled={busy || !sessionId}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <button
              type="button"
              className="lm-cta"
              onClick={openAdd}
              disabled={busy || !sessionId}
            >
              <FaPlus aria-hidden />
              Add leave
            </button>
          </div>
        </header>

        <div className="lm-desk">
          <aside className="lm-rail" aria-label="Filters">
            <div className="lm-kpis">
              <button
                type="button"
                className={`lm-kpi is-attendance${
                  statusTab === "attendance" ? " is-on" : ""
                }`}
                onClick={() => setStatusTab("attendance")}
              >
                <em>{counts.attendance}</em>
                <span>No application</span>
              </button>
              <button
                type="button"
                className={`lm-kpi is-pending${
                  statusTab === "pending" ? " is-on" : ""
                }`}
                onClick={() => setStatusTab("pending")}
              >
                <em>{counts.pending}</em>
                <span>Pending</span>
              </button>
              <button
                type="button"
                className={`lm-kpi is-approved${
                  statusTab === "approved" ? " is-on" : ""
                }`}
                onClick={() => setStatusTab("approved")}
              >
                <em>{counts.approved}</em>
                <span>Approved</span>
              </button>
              <button
                type="button"
                className={`lm-kpi is-rejected${
                  statusTab === "rejected" ? " is-on" : ""
                }`}
                onClick={() => setStatusTab("rejected")}
              >
                <em>{counts.rejected}</em>
                <span>Rejected</span>
              </button>
            </div>

            <p className="lm-rail__label">Status</p>
            <div className="lm-rail__stack" role="tablist" aria-label="Leave status">
              {[
                { id: "attendance", label: "No application" },
                { id: "pending", label: "Pending" },
                { id: "approved", label: "Approved" },
                { id: "rejected", label: "Rejected" },
                { id: "all", label: "Show all" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={statusTab === tab.id}
                  className={`lm-rail-btn is-${tab.id}${
                    statusTab === tab.id ? " is-active" : ""
                  }`}
                  onClick={() => setStatusTab(tab.id)}
                >
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </aside>

          <section className="lm-inbox" aria-label="Leave applications">
            <div className="lm-inbox__bar">
              <div className="min-w-0">
                <h2 className="lm-inbox__title">
                  {statusTab === "all"
                    ? "All applications"
                    : statusTab === "attendance"
                    ? "Leave without application"
                    : `${statusTab.charAt(0).toUpperCase()}${statusTab.slice(
                        1
                      )} inbox`}
                </h2>
                <p className="lm-inbox__hint">
                  {filtered.length} shown
                  {typeTab !== "all" ? ` · ${typeTab}` : ""}
                </p>
              </div>
              <input
                type="search"
                className="lm-search"
                placeholder="Search name, subject…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search leave"
              />
            </div>

            {loading && !filtered.length ? (
              <div className="lm-loading">
                <MyLoader />
              </div>
            ) : null}

            {!loading && !filtered.length ? (
              <div className="lm-empty">
                <div className="lm-empty__mark" aria-hidden>
                  <FaUmbrellaBeach />
                </div>
                <p className="lm-empty__title">
                  {search.trim()
                    ? "Nothing matches this search"
                    : "Inbox is clear"}
                </p>
                <p className="lm-empty__hint">
                  {sessionId
                    ? "Submit a leave or switch filters on the left."
                    : "Select a session to load applications."}
                </p>
                {sessionId ? (
                  <button
                    type="button"
                    className="lm-empty__cta"
                    onClick={openAdd}
                  >
                    <FaPlus aria-hidden /> Add leave
                  </button>
                ) : null}
              </div>
            ) : null}

            {filtered.length ? (
              <div className="lm-feed">
                {filtered.map((item) => (
                  <LeaveCard
                    key={`${item._type}-${item.application_id}`}
                    item={item}
                    busy={busy}
                    onView={setDetailItem}
                    onAddApplication={openAddFromAttendance}
                  />
                ))}
              </div>
            ) : null}
          </section>
        </div>
      </div>

      <MdlLeaveDetails
        open={!!detailItem}
        item={detailItem}
        busy={!!busyId}
        onClose={() => setDetailItem(null)}
        onApprove={(item) => updateStatus(item, STATUS.APPROVED)}
        onReject={(item) => updateStatus(item, STATUS.REJECTED)}
        onAddApplication={openAddFromAttendance}
      />

      <MdlAddLeave
        open={addOpen}
        form={form}
        errors={formErrors}
        saving={saving}
        onChange={onFormChange}
        onDayCountBlur={onDayCountBlur}
        onClose={() => {
          if (!saving) {
            setAddOpen(false);
            setFormErrors({});
          }
        }}
        onSave={saveLeave}
        onPickStudent={() => setPickStudentOpen(true)}
        onPickStaff={() => setPickStaffOpen(true)}
      />

      {sessionDialogOpen ? (
        <MdlSelectSession
          action={() => setSessionDialogOpen(false)}
          action2={onSessionPicked}
        />
      ) : null}

      {pickStudentOpen ? (
        <MdlSelectSingleStudent
          action={() => setPickStudentOpen(false)}
          action2={(enrollmentNo, student) => {
            setForm((prev) => ({
              ...prev,
              applicantType: "Student",
              applicantId: enrollmentNo || student?.student_enrollment_no || "",
              applicantName: student?.stu_name || enrollmentNo || "",
            }));
            setFormErrors((prev) => {
              if (!prev.applicantId) return prev;
              const next = { ...prev };
              delete next.applicantId;
              return next;
            });
            setPickStudentOpen(false);
          }}
        />
      ) : null}

      {pickStaffOpen ? (
        <MdlSelectSingleStaff
          actionClose={() => setPickStaffOpen(false)}
          actionHandleSelected={(staff) => {
            setForm((prev) => ({
              ...prev,
              applicantType: "Staff",
              applicantId: staff?.staff_id || "",
              applicantName: staff?.staff_name || staff?.staff_id || "",
            }));
            setFormErrors((prev) => {
              if (!prev.applicantId) return prev;
              const next = { ...prev };
              delete next.applicantId;
              return next;
            });
            setPickStaffOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
